import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, useState } from 'react';

import { OrderbookRow } from './OrderbookRow';

import { VirtualizedListProps } from '../model/types';

const ROW_HEIGHT = 20; // Fixed row height for virtualization
const OVERSCAN = 5; // Number of items to render outside visible area

/**
 * High-performance virtualized orderbook list with Gate.io-level smoothness
 *
 * Optimizations:
 * - Uses @tanstack/react-virtual for rendering only visible items
 * - Price-based React keys for stable DOM elements
 * - Smooth CSS transitions (200-300ms)
 * - Auto-scroll for asks (keeps lowest ask visible)
 * - Hover range highlighting with smooth transitions
 *
 * Correct Asks/Bids Layout (Binance-style):
 * - Asks: ascending order [low→high] + PREPENDED placeholders + auto-scroll to bottom
 *   → Array: [0.3440, 0.3441, ..., 0.3450]
 *   → Bottom of scroll: 0.3440 (lowest ask, near spread) ✓
 * - Bids: descending order [high→low] + APPENDED placeholders
 *   → Array: [0.3420, 0.3419, ..., 0.3407]
 *   → Top: 0.3420 (highest bid, near spread) ✓
 * - Virtualization uses absolute positioning for smooth scrolling
 * - Fixed depth (30 or 50) maintained for stable layout
 */
export function VirtualizedOrderbookList({
  type,
  data,
  height,
  lastPrice: _lastPrice,
  onRowClick,
  onRowHover,
  hoveredIndex,
  onScrollingChange,
}: VirtualizedListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const scrollTimeoutRef = useRef<number | null>(null);
  const isScrollingRef = useRef(false);
  // Flag to suppress scroll handler during programmatic auto-scroll
  const isProgrammaticScrollRef = useRef(false);

  // Initialize virtualizer
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const viewportHeight = height ?? 0;
  const totalSize = virtualizer.getTotalSize();
  const isAskSide = type === 'ask';

  // For asks, filter out placeholders and use bottom alignment if data fits in viewport (Binance-style)
  // This ensures asks stick to the spread/center when there are few orders
  const realData = isAskSide ? data.filter(entry => !entry.isPlaceholder) : data;
  const realDataSize = realData.length * ROW_HEIGHT;
  const needsBottomAlignment = isAskSide && realDataSize <= viewportHeight;

  // Calculate top padding for asks to align them to the bottom (near spread)
  // This creates the Binance-style behavior where asks stick to the center/spread
  const topPadding = isAskSide && totalSize < viewportHeight ? viewportHeight - totalSize : 0;

  // Track scrolling state and auto-scroll position for asks
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    const handleScroll = () => {
      // Skip updating scroll state during programmatic auto-scroll to prevent tooltip flicker
      if (isProgrammaticScrollRef.current) {
        isProgrammaticScrollRef.current = false;
        return;
      }

      isScrollingRef.current = true;
      onScrollingChange?.(true);
      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = window.setTimeout(() => {
        isScrollingRef.current = false;
        onScrollingChange?.(false);
        scrollTimeoutRef.current = null;
      }, 100);

      // Check if user is at bottom (for asks auto-scroll)
      const OFFSET = 20;
      const isAtBottom = Math.abs(el.scrollHeight - el.scrollTop) <= el.clientHeight + OFFSET;
      setIsAutoScroll(isAtBottom);
    };

    el.addEventListener('scroll', handleScroll);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [onScrollingChange]);

  // Auto-scroll asks to bottom (shows lowest ask at bottom, near last price)
  // Bids don't need auto-scroll (highest bid naturally at top)
  useEffect(() => {
    const el = parentRef.current;
    if (el && isAutoScroll && type === 'ask') {
      // Set flag BEFORE programmatic scroll to suppress the scroll event handler
      // This prevents tooltip flickering during auto-scroll
      isProgrammaticScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
      // Clear flag unconditionally after scroll completes
      // If already at bottom, no scroll event fires, so we must clear manually
      // Use queueMicrotask to ensure this runs after any synchronous scroll event
      queueMicrotask(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
  }, [data, type, isAutoScroll]);

  const handleRowClick = useCallback(
    (index: number) => {
      const entry = data[index];
      if (entry && onRowClick) {
        // Calculate cumulative amount for selected range
        const rangeData = type === 'ask' ? data.slice(index) : data.slice(0, index + 1);
        const cumulativeAmount = rangeData
          .reduce((sum, item) => sum + Number(item.amount), 0)
          .toString();
        onRowClick(entry.price, cumulativeAmount);
      }
    },
    [data, type, onRowClick],
  );

  // Handle row hover - use direct element reference from event
  const handleRowHover = useCallback(
    (index: number, element: HTMLElement | null) => {
      // Always update hover state with fresh element reference
      // The parent will decide whether to show tooltip based on isScrolling
      if (onRowHover) {
        onRowHover(index, element);
      }
    },
    [onRowHover],
  );

  const handleMouseLeave = useCallback(() => {
    if (onRowHover) {
      onRowHover(null, null);
    }
  }, [onRowHover]);

  // Check if index is in hovered range
  const isInHoveredRange = useCallback(
    (index: number): boolean => {
      if (hoveredIndex === null) return false;
      if (type === 'ask') {
        return index >= hoveredIndex;
      } else {
        return index <= hoveredIndex;
      }
    },
    [hoveredIndex, type],
  );

  return (
    <div
      ref={parentRef}
      className="overflow-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700"
      style={{ height: `${height}px` }}
      onMouseLeave={handleMouseLeave}
    >
      {needsBottomAlignment ? (
        <div className="flex h-full flex-col justify-end">
          {realData.map(entry => {
            // Find the actual index in the original data array (which includes placeholders)
            // This ensures hover/click handlers use the correct index for tooltip calculations
            // Note: actualIndex should always be valid since realData is filtered from data
            const actualIndex = data.findIndex(d => d.price === entry.price);

            // Skip rendering if entry not found in data (defensive - shouldn't happen)
            if (actualIndex === -1) return null;

            return (
              <div
                key={entry.price}
                style={{ height: `${ROW_HEIGHT}px` }}
                className="relative"
                onMouseEnter={e => handleRowHover(actualIndex, e.currentTarget)}
              >
                <OrderbookRow
                  entry={entry}
                  type={type}
                  isHovered={hoveredIndex === actualIndex}
                  isInHoveredRange={isInHoveredRange(actualIndex)}
                  onClick={() => handleRowClick(actualIndex)}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            height: `${Math.max(totalSize, viewportHeight)}px`,
            width: '100%',
            position: 'relative',
            // Note: flex-direction does NOT work with virtualization (uses absolute positioning)
            // For asks with small lists, we add topPadding to translateY of each item
          }}
        >
          {virtualizer.getVirtualItems().map(virtualRow => {
            const entry = data[virtualRow.index];
            if (!entry) return null;

            // CRITICAL: Use price as key instead of index for stable rows
            // This prevents React from re-mounting rows when prices shift positions
            // Gate.io achieves smoothness by keeping DOM elements stable
            // Don't enable hover/click for placeholder rows
            const isPlaceholder = entry.isPlaceholder === true;

            return (
              <div
                key={entry.price}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  // Add topPadding to translateY for asks to push them towards spread (Binance-style)
                  transform: `translateY(${virtualRow.start + topPadding}px)`,
                  // Smooth position transitions when rows shift
                  transition: 'transform 150ms ease-out',
                }}
                onMouseEnter={
                  isPlaceholder ? undefined : e => handleRowHover(virtualRow.index, e.currentTarget)
                }
              >
                <OrderbookRow
                  entry={entry}
                  type={type}
                  isHovered={hoveredIndex === virtualRow.index}
                  isInHoveredRange={isInHoveredRange(virtualRow.index)}
                  onClick={() => handleRowClick(virtualRow.index)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
